import type { SubmitQuizRequest } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPublicQuizConditionValues,
  getPublicQuizzes,
  startPublicQuiz,
  submitPublicQuiz,
} from "@/lib/api/public-flow/quiz";
import { useSessionToken } from "@/lib/context/public-session-context";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";

/** Instance-free and "publicFlow"-prefixed: one session, one instance. */
export const publicQuizKeys = {
  quizzes: ["publicFlow", "quiz", "quizzes"] as const,
  conditionValues: ["publicFlow", "quiz", "condition-values"] as const,
};

/**
 * QL-698 — the `__quiz_score_<id>` condition values, resolved on the server
 * from the full value, so a member's session reads the whole group's score
 * range. Refetched on submit. Another member's attempt shows up only after this
 * session's own next submit or a reload (window-focus refetch is off app-wide)
 * — Submit is checked server-side anyway.
 */
export function usePublicQuizConditionValues() {
  const token = useSessionToken();
  const { data } = useQuery({
    queryKey: publicQuizKeys.conditionValues,
    queryFn: () => getPublicQuizConditionValues(token),
    enabled: !!token,
  });
  return data?.values ?? null;
}

export function usePublicQuizzes() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: publicQuizKeys.quizzes,
    queryFn: () => getPublicQuizzes(token),
    enabled: !!token,
  });
  return { quizzes: data ?? null, error, isLoading };
}

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function useStartPublicQuiz() {
  const token = useSessionToken();
  return useMutation({
    mutationFn: ({ quizId, subjectId }: { quizId: string; subjectId?: string }) =>
      startPublicQuiz(token, quizId, subjectId),
  });
}

export function useSubmitPublicQuiz() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SubmitQuizRequest) => submitPublicQuiz(token, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicQuizKeys.quizzes });
      queryClient.invalidateQueries({ queryKey: publicQuizKeys.conditionValues });
      // Completion semantics — keep the public progress bar and the Submit
      // gate live (P5).
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}
